import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Button,
  FormControl,
  InputLabel,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { CSVLink } from "react-csv";

import DownloadIcon from "@mui/icons-material/Download";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { planNames } from "config";

import {
  useGetCampaignsAnalyticsQuery,
  useGetUsersCampaignsAnalyticsQuery,
} from "services/analytics";
import AnalyticsUsersTable from "../AnalyticsUsersTable";

dayjs.extend(relativeTime);

const STATUS_META = {
  totalCampaigns: {
    label: "Total campaigns",
    icon: CampaignIcon,
    color: "#1976d2",
  },
  active: {
    label: "Active campaigns",
    icon: CheckCircleIcon,
    color: "#2e7d32",
  },
  paused: {
    label: "Paused campaigns",
    icon: PauseCircleIcon,
    color: "#ed6c02",
  },
  draft: { label: "Draft campaigns", icon: ScheduleIcon, color: "#0288d1" },
  completed: {
    label: "Completed campaigns",
    icon: CheckCircleIcon,
    color: "#388e3c",
  },
  error: { label: "Error campaigns", icon: ErrorOutlineIcon, color: "#d32f2f" },
  exhausted: {
    label: "Exhausted campaigns",
    icon: ReportProblemIcon,
    color: "#6d6d6d",
  },
  connectedEmailAccounts: {
    label: "Connected Email Accounts",
    icon: EmailIcon,
    color: "#6a1b9a",
  },
  connectedLinkedinAccounts: {
    label: "Connected LinkedIn Accounts",
    icon: LinkedInIcon,
    color: "#0A66C2",
  },
  totalEmailsSent: {
    label: "Total Emails Sent",
    icon: SendIcon,
    color: "#1565c0",
  },
  totalDfyInfraSetups: {
    label: "Total DFY",
    icon: AutoAwesomeIcon,
    color: "#61356dff",
  },
};

const usageOptions = [
  { value: "campaigns_gt_0", label: "Campaigns > 0" },
  { value: "campaigns_gt_10", label: "Active Campaigns > 10" },
  { value: "emails_gt_1000", label: "Emails Sent > 1,000" },
  { value: "emails_gt_10000", label: "Emails Sent > 10,000" },
  { value: "has_email_accounts", label: "Has Email Accounts" },
  { value: "has_linkedin_accounts", label: "Has LinkedIn Accounts" },
];

const CampaignAnalytics = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [usageFilter, setUsageFilter] = useState([]); // applied filters
  const [tempUsageFilter, setTempUsageFilter] = useState([]); // temporary selection
  const [open, setOpen] = useState(false);
  const [exportData, setExportData] = useState([]);

  const csvButton = useRef();

  const queryParams = useMemo(
    () => ({
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    }),
    [startDate, endDate],
  );

  const { data, isLoading, isError } = useGetCampaignsAnalyticsQuery();

  const {
    data: usersAnalytics = [],
    isLoading: isUsersLoading,
    isError: isUsersError,
    refetch: refetchUsersForExport,
  } = useGetUsersCampaignsAnalyticsQuery({
    ...queryParams,
    page,
    limit: 10,
    search: debouncedSearch || undefined,
    usageFilter: usageFilter.length ? usageFilter : undefined,
  });

  const { metrics = {} } = data || {};
  const refreshedText = data?.generatedAt
    ? `Refreshed ${dayjs(data.generatedAt).fromNow()}`
    : null;

  const buildCampaignAnalyticsCards = (summaryData = {}) =>
    Object.entries(summaryData).map(([key, value]) => {
      const meta = STATUS_META[key] || {
        label: key.replace(/([A-Z])/g, " $1"),
        icon: CampaignIcon,
        color: "#9e9e9e",
      };
      return {
        key,
        label: meta.label,
        value: value ?? 0,
        icon: meta.icon,
        color: meta.color,
      };
    });

  const analyticsCards = buildCampaignAnalyticsCards(metrics);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // reset pagination on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const handleExportCSV = async () => {
    try {
      const response = await refetchUsersForExport();

      const rows = response?.data?.data || [];

      const flattened = rows.map((row) => ({
        name: `${row.name?.first || ""} ${row.name?.last || ""}`,
        email: row.email || "",
        totalCampaigns: row.campaigns?.totalCampaigns ?? 0,
        active: row.campaigns?.active ?? 0,
        paused: row.campaigns?.paused ?? 0,
        draft: row.campaigns?.draft ?? 0,
        completed: row.campaigns?.completed ?? 0,
        error: row.campaigns?.error ?? 0,
        exhausted: row.campaigns?.exhausted ?? 0,
        connectedEmailAccounts: row.connectedEmailAccounts ?? 0,
        connectedLinkedinAccounts: row.connectedLinkedinAccounts ?? 0,
        totalEmailsSent: row.totalEmailsSent ?? 0,
        totalDfy: row.totalDfy ?? 0,
      }));

      setExportData(flattened);

      setTimeout(() => {
        csvButton.current?.link?.click();
      }, 0);
    } catch (err) {
      alert("Failed to export CSV");
    }
  };

  if (isLoading) return <Typography>Loading analytics...</Typography>;
  if (isError)
    return <Typography color="error">Failed to load analytics</Typography>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end">
          {refreshedText && (
            <Typography variant="caption" color="text.secondary">
              {refreshedText}
            </Typography>
          )}
        </Box>
      </Grid>

      {/* Analytics Cards */}
      {analyticsCards.map((item) => {
        const Icon = item.icon;
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderLeft: `5px solid ${item.color}`,
              }}
            >
              <Box sx={{ color: item.color }}>
                <Icon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h6">{item.value}</Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}

      {/* Filters (for users table) */}
      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
            sx={{ ml: 2 }}
          />
        </LocalizationProvider>

        <TextField
          label="Search by name or email"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 300, ml: 2 }}
        />

        <FormControl size="small" sx={{ minWidth: 280, ml: 2 }}>
          <InputLabel>Usage Filter</InputLabel>

          <Select
            multiple
            open={open}
            onOpen={() => {
              setTempUsageFilter(usageFilter);
              setOpen(true);
            }}
            onClose={() => setOpen(false)}
            value={usageFilter}
            label="Usage Filter"
            renderValue={(selected) =>
              selected.length === 0
                ? "All"
                : `${selected.length} filter(s) selected`
            }
          >
            {usageOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                onClick={() => {
                  if (tempUsageFilter.includes(option.value)) {
                    setTempUsageFilter(
                      tempUsageFilter.filter((v) => v !== option.value),
                    );
                  } else {
                    setTempUsageFilter([...tempUsageFilter, option.value]);
                  }
                }}
              >
                <Checkbox checked={tempUsageFilter.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}

            {/* Footer Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderTop: "1px solid #eee",
              }}
            >
              <Button
                size="small"
                onClick={() => {
                  setTempUsageFilter([]);
                  setUsageFilter([]);
                  setOpen(false);
                }}
              >
                Clear
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setUsageFilter(tempUsageFilter);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </Box>
          </Select>
        </FormControl>
      </Grid>

      {/* Export Button Row */}
      <Grid item xs={12}>
        <CSVLink
          ref={csvButton}
          data={exportData}
          style={{ display: "none" }}
          filename={`campaign_analytics_${dayjs().format("YYYY-MM-DD")}.csv`}
        />
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        </Box>
      </Grid>

      {/* Users Analytics Table */}
      <Grid item xs={12}>
        <AnalyticsUsersTable
          columns={[
            {
              key: "name",
              label: "USER",
              render: (row) => `${row.name.first} ${row.name.last}`,
            },
            { key: "email", label: "EMAIL" },
            {
              key: "leadsPlan",
              label: "LEADS PLAN",
              align: "center",
              render: (row) =>
                row.currentPlan?.subscription?.leads?.active
                  ? planNames[row.currentPlan?.subscription?.leads?.planId]
                  : "-",
            },
            {
              key: "sendingWarmupPlan",
              label: "SENDING WARMUP PLAN",
              align: "center",
              render: (row) =>
                row.currentPlan?.subscription?.sendingWarmup?.active
                  ? planNames[
                      row.currentPlan?.subscription?.sendingWarmup?.planId
                    ]
                  : "-",
            },
            {
              key: "totalCampaigns",
              label: "TOTAL",
              align: "center",
              render: (row) => row.campaigns.totalCampaigns,
            },
            {
              key: "active",
              label: "ACTIVE",
              align: "center",
              render: (row) => row.campaigns.active,
            },
            {
              key: "paused",
              label: "PAUSED",
              align: "center",
              render: (row) => row.campaigns.paused,
            },
            {
              key: "draft",
              label: "DRAFT",
              align: "center",
              render: (row) => row.campaigns.draft,
            },
            {
              key: "completed",
              label: "COMPLETED",
              align: "center",
              render: (row) => row.campaigns.completed,
            },
            {
              key: "error",
              label: "ERROR",
              align: "center",
              render: (row) => row.campaigns.error,
            },
            {
              key: "exhausted",
              label: "EXHAUSTED",
              align: "center",
              render: (row) => row.campaigns.exhausted,
            },
            {
              key: "connectedEmailAccounts",
              label: "EMAIL ACCOUNTS",
              align: "center",
              render: (row) => row.connectedEmailAccounts ?? 0,
            },
            {
              key: "connectedLinkedinAccounts",
              label: "LINKEDIN ACCOUNTS",
              align: "center",
              render: (row) => row.connectedLinkedinAccounts ?? 0,
            },
            {
              key: "totalEmailsSent",
              label: "EMAILS SENT",
              align: "center",
              render: (row) => row.totalEmailsSent ?? 0,
            },
            {
              key: "totalDfy",
              label: "TOTAL DFY",
              align: "center",
              render: (row) => row.totalDfy ?? 0,
            },
          ]}
          rows={usersAnalytics?.data || []}
          loading={isUsersLoading}
          error={isUsersError}
          pagination={usersAnalytics?.pagination}
          onPageChange={setPage}
        />
      </Grid>
    </Grid>
  );
};

export default CampaignAnalytics;
